import { formatCurrencyAmount } from '@/common/domain/kit/money';
import { EditableLineCell } from '@/document-render/presentation/blocks/lineItems/EditableLineCell';
import { LineItemActionsMenu } from '@/document-render/presentation/blocks/lineItems/LineItemActionsMenu';
import { LINE_ITEM_COLUMN as COLUMN, lineItemBodyCellStyle } from '@/document-render/presentation/blocks/lineItems/lineItemsQuoteStyles';

export interface QuoteLineItem {
  id?: string;
  description?: string;
  quantity?: number | null;
  rate?: number | null;
  unit?: string;
  origin?: string;
  locked?: boolean;
}

function isDerived(row: QuoteLineItem): boolean {
  return row.origin === 'price_book' || row.locked === true;
}

export function LineItemQuoteRow({
  row,
  onEditCell,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  currency,
  cultureTag
}: {
  row: QuoteLineItem;
  onEditCell?: (rowId: string, columnKey: string, value: string) => void;
  onDelete?: (rowId: string) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** ISO 4217 code and culture tag the Amount (and a read-only rate) are formatted with. */
  currency: string;
  cultureTag: string;
}) {
  const readOnly = typeof row.id !== 'string' || !row.id;
  const derived = isDerived(row);
  const description = row.description ?? '';
  const commit = (columnKey: string, value: string) => {
    if (readOnly || !row.id || !onEditCell) return;
    onEditCell(row.id, columnKey, value);
  };

  return (
    <tr>
      <td style={lineItemBodyCellStyle(COLUMN.description.align)}>
        <EditableLineCell
          label="Edit description"
          value={description}
          editable={!readOnly && !derived}
          emptyMarker="add"
          align={COLUMN.description.align}
          onCommit={(value) => commit('description', value)}
        />
      </td>
      <td style={lineItemBodyCellStyle(COLUMN.quantity.align)}>
        <EditableLineCell
          label="Edit quantity"
          value={row.quantity == null ? '' : String(row.quantity)}
          editable={!readOnly}
          emptyMarker={derived ? 'dash' : 'add'}
          align={COLUMN.quantity.align}
          onCommit={(value) => commit('quantity', value)}
        />
      </td>
      <td style={lineItemBodyCellStyle(COLUMN.unit.align)}>
        <EditableLineCell
          label="Edit unit"
          value={row.unit ?? ''}
          editable={!readOnly && !derived}
          emptyMarker="add"
          align={COLUMN.unit.align}
          onCommit={(value) => commit('unit', value)}
        />
      </td>
      <td style={lineItemBodyCellStyle(COLUMN.rate.align)}>
        <EditableLineCell
          label="Edit rate"
          value={row.rate == null ? '' : String(row.rate)}
          editable={!readOnly && !derived}
          displayText={row.rate == null ? undefined : formatCurrencyAmount(row.rate, currency, cultureTag)}
          align={COLUMN.rate.align}
          onCommit={(value) => commit('rate', value)}
        />
      </td>
      <td style={lineItemBodyCellStyle(COLUMN.amount.align)}>
        {row.rate == null || row.quantity == null ? '—' : formatCurrencyAmount(row.rate * row.quantity, currency, cultureTag)}
      </td>
      <td style={lineItemBodyCellStyle(COLUMN.actions.align)}>
        {readOnly ? null : (
          <LineItemActionsMenu
            label={`More actions — ${description || 'line item'}`}
            actions={[
              { label: 'Move up', disabled: !canMoveUp, onSelect: () => onMoveUp?.() },
              { label: 'Move down', disabled: !canMoveDown, onSelect: () => onMoveDown?.() },
              { label: 'Delete', onSelect: () => row.id && onDelete?.(row.id) }
            ]}
          />
        )}
      </td>
    </tr>
  );
}
